import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Mic } from "lucide-react";
import { INSIGHTS } from "@/data/insights";
import { ASSETS } from "@/data/assets";
import NewsletterForm from "@/components/NewsletterForm";

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

export default function Insights() {
  const [featured, ...rest] = INSIGHTS;
  return (
    <div data-testid="page-insights" className="mx-auto max-w-7xl px-6 lg:px-10 py-16 lg:py-24">
      {/* Speaking banner */}
      <div className="relative w-full aspect-[21/9] sm:aspect-[24/9] lg:aspect-[28/10] overflow-hidden border border-border mb-14">
        <img
          src={ASSETS.speaking}
          alt="Vincent speaking at a conference"
          loading="eager"
          decoding="async"
          className="w-full h-full object-cover"
          style={{ objectPosition: "50% 30%" }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/60 to-transparent" />
        <div className="absolute inset-0 flex items-end p-6 sm:p-10 lg:p-14">
          <div className="max-w-xl">
            <p className="font-mono-ui text-[11px] uppercase tracking-[0.28em] text-accent flex items-center gap-2">
              <Mic className="h-3.5 w-3.5" />
              // On stage
            </p>
            <h2 className="mt-3 font-display font-black text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight">
              Ideas that travel — from keynote to inbox.
            </h2>
          </div>
        </div>
      </div>

      <p className="font-mono-ui text-[11px] uppercase tracking-[0.28em] text-accent">
        // Insights
      </p>
      <h1 className="mt-4 font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02] max-w-4xl">
        Weekly notes on leadership, AI, and Corporate Athlete 2.0.
      </h1>
      <p className="mt-6 text-lg text-muted-foreground max-w-2xl leading-relaxed">
        Essays and short posts that support the framework and the book.
        Practical. Compounding. No theater.
      </p>

      <div className="hairline-ai my-14" />

      {/* Newsletter — inline capture at top of feed */}
      <div className="grid lg:grid-cols-12 gap-6 items-center border border-border bg-background p-6 lg:p-8 mb-12">
        <div className="lg:col-span-6">
          <p className="font-mono-ui text-[11px] uppercase tracking-[0.25em] text-ai">
            // Weekly essays
          </p>
          <h2 className="mt-2 font-display font-bold text-2xl tracking-tight">
            Get every new insight in your inbox.
          </h2>
          <p className="mt-2 text-sm text-muted-foreground max-w-lg">
            Corporate Athlete 2.0 essays, delivered every Sunday. Short, practical, no fluff.
          </p>
        </div>
        <div className="lg:col-span-6 lg:justify-self-end w-full">
          <NewsletterForm source="insights-top" variant="inline" />
        </div>
      </div>

      {/* Featured */}
      {featured && (
        <Link
          to={`/insights/${featured.slug}`}
          data-testid="insights-featured-card"
          className="group grid lg:grid-cols-12 gap-8 border border-border bg-background overflow-hidden hover:-translate-y-1 transition-transform"
        >
          <div className="lg:col-span-7 aspect-[16/10] lg:aspect-auto overflow-hidden bg-secondary">
            <img
              src={featured.hero}
              alt={featured.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          </div>
          <div className="lg:col-span-5 p-8 lg:p-10 flex flex-col justify-center">
            <p className="font-mono-ui text-[10px] uppercase tracking-widest text-accent">
              Featured · {featured.tag}
            </p>
            <h2 className="mt-3 font-display font-black text-3xl lg:text-4xl tracking-tight leading-tight">
              {featured.title}
            </h2>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              {featured.excerpt}
            </p>
            <div className="mt-6 flex items-center gap-4 text-xs font-mono-ui text-muted-foreground">
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="h-3 w-3" />
                {formatDate(featured.date)}
              </span>
              <span>{featured.readTime}</span>
            </div>
            <span className="mt-6 inline-flex items-center gap-2 text-sm font-medium">
              Read essay
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </span>
          </div>
        </Link>
      )}

      {/* Grid */}
      <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {rest.map((p) => (
          <Link
            key={p.slug}
            to={`/insights/${p.slug}`}
            data-testid={`insights-card-${p.slug}`}
            className="group border border-border bg-background overflow-hidden hover:-translate-y-1 transition-transform"
          >
            <div className="aspect-[16/10] overflow-hidden bg-secondary">
              <img
                src={p.hero}
                alt={p.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="p-6">
              <p className="font-mono-ui text-[10px] uppercase tracking-widest text-accent">
                {p.tag}
              </p>
              <h3 className="mt-3 font-display font-bold text-lg leading-tight">
                {p.title}
              </h3>
              <p className="mt-2 text-sm text-muted-foreground line-clamp-3">
                {p.excerpt}
              </p>
              <div className="mt-4 flex items-center justify-between text-xs font-mono-ui text-muted-foreground">
                <span>{formatDate(p.date)}</span>
                <span>{p.readTime}</span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
